import { Tabs } from "@mantine/core";
import { FC, useEffect, useState } from "react";
import {
	ChartBarIcon,
	TrophyIcon,
	CalendarIcon,
	GlobeAltIcon,
	BoltIcon,
	ShieldCheckIcon,
	Squares2X2Icon
} from "@heroicons/react/24/outline";
import TeamStats from "../components/TeamStats";
import Insights from "../components/insights/Insights";
import { useParams, useSearchParams } from "react-router-dom";
import { getOrgId } from "../utils/orgIdMapper";
import Header from "../components/shared/Header";
import { ClubProfile, getClubProfile } from "../cricket-api/team-profile";
import Footer from "../components/shared/Footer";
import SeasonInsights from "../components/season/SeasonInsights";
import SeasonDashboard from "../components/dashboard/SeasonDashboard";

type StatsTab = "dashboard" | "insights" | "season-insights" | "statistics";

const VALID_TABS: StatsTab[] = ["dashboard", "insights", "season-insights", "statistics"];

const PC_LOGO =
	"https://resources.playcommunity.pulselive.com/playcommunity/document/2023/06/23/5638dcb6-e417-4d0c-a626-0bf1512c755d/Play-Cricket-Crest.svg";

const Stats: FC = () => {
	const [profile, setProfile] = useState<ClubProfile | null>(null);
	const [loading, setLoading] = useState(true);
	const params = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const clubName = params.clubName;
	const tabParam = searchParams.get("tab");
	const activeTab: StatsTab = VALID_TABS.includes(tabParam as StatsTab)
		? (tabParam as StatsTab)
		: "dashboard";

	const setActiveTab = (nextTab: string | null) => {
		const resolvedTab: StatsTab = VALID_TABS.includes(nextTab as StatsTab)
			? (nextTab as StatsTab)
			: "dashboard";

		setSearchParams(
			prev => {
				const next = new URLSearchParams(prev);
				next.set("tab", resolvedTab);
				return next;
			},
			{ replace: true }
		);
	};

	useEffect(() => {
		if (VALID_TABS.includes(tabParam as StatsTab)) {
			return;
		}

		setSearchParams(
			prev => {
				const next = new URLSearchParams(prev);
				next.set("tab", "dashboard");
				return next;
			},
			{ replace: true }
		);
	}, [tabParam, setSearchParams]);

	useEffect(() => {
		const orgId = getOrgId(clubName || "");
		if (orgId) {
			setLoading(true);
			getClubProfile(orgId).then(profile => {
				setProfile(profile);
				setLoading(false);
			});
		} else {
			setLoading(false);
		}
	}, [clubName]);

	if (!clubName) {
		return (
			<div className="min-h-screen">
				<Header />
				<div className="flex items-center justify-center min-h-[60vh]">
					<div className="text-center p-8 panel rounded-2xl max-w-xl mx-4">
						<div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
							<span className="text-red-600 text-2xl">⚠️</span>
						</div>
						<h2 className="text-4xl text-white mb-2">Club Not Found</h2>
						<p className="text-slate-300">This club is not registered in our system.</p>
					</div>
				</div>
			</div>
		);
	}

	const orgId = getOrgId(clubName);
	if (!orgId) {
		return (
			<div className="min-h-screen">
				<Header />
				<div className="flex items-center justify-center min-h-[60vh]">
					<div className="text-center p-8 panel rounded-2xl max-w-xl mx-4">
						<div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
							<span className="text-red-600 text-2xl">⚠️</span>
						</div>
						<h2 className="text-4xl text-white mb-2">Club Not Registered</h2>
						<p className="text-slate-300">This club is not available in our analytics platform.</p>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen">
			<Header />

			<section className="relative overflow-hidden border-b border-slate-700/70">
				<div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_15%_18%,rgba(255,159,28,.16),transparent_35%),radial-gradient(circle_at_85%_8%,rgba(29,211,176,.14),transparent_28%)]"></div>
				<div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
					{loading ? (
						<div className="flex items-center justify-center py-12">
							<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400"></div>
						</div>
					) : (
						<div className="panel rounded-2xl p-5 md:p-8 flex flex-col md:flex-row items-center justify-between gap-5 md:gap-6">
							<div className="flex items-center gap-3 sm:gap-6 w-full md:w-auto">
								<div className="relative">
									<img
										alt="Play Cricket Logo"
										src={PC_LOGO}
										className="h-12 w-12 sm:h-16 sm:w-16 rounded-full bg-gradient-to-br from-cyan-600 to-blue-800 p-2 shadow-lg"
									/>
									<div className="absolute -top-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
										<GlobeAltIcon className="h-3 w-3 text-white" />
									</div>
								</div>

								<div className="text-left min-w-0">
									<h1 className="text-3xl sm:text-5xl md:text-6xl text-white mb-2 truncate">
										{profile?.Name || clubName}
									</h1>
									<p className="text-slate-300 text-base md:text-xl">Performance Command Center</p>
									<div className="mt-3 flex flex-wrap items-center gap-2">
										<div className="inline-flex items-center gap-1 rounded-full border border-slate-600 px-3 py-1 text-xs text-slate-200">
											<BoltIcon className="h-3 w-3 text-orange-300" />
											Live analytics
										</div>
										<div className="inline-flex items-center gap-1 rounded-full border border-slate-600 px-3 py-1 text-xs text-slate-200">
											<ShieldCheckIcon className="h-3 w-3 text-emerald-300" />
											Verified source
										</div>
									</div>
								</div>

								{profile?.LogoURL && (
									<div>
										<img
											alt="Club logo"
											src={profile.LogoURL}
											className="h-12 w-12 sm:h-16 sm:w-16 rounded-full shadow-lg ring-4 ring-slate-600"
										/>
									</div>
								)}
							</div>
						</div>
					)}
				</div>
			</section>

			<div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">
				<div>
					<Tabs className="w-full" value={activeTab} onChange={setActiveTab}>
						<div className="mb-8">
							<Tabs.List className="panel rounded-xl p-2 grid grid-cols-2 md:flex md:flex-wrap justify-center md:justify-start gap-2">
								<Tabs.Tab
									value="dashboard"
									className="w-full md:w-auto flex items-center justify-center space-x-2 px-2 sm:px-5 py-2.5 rounded-lg font-medium text-sm sm:text-base text-slate-200 transition-colors duration-150 data-[active]:bg-yellow-300 data-[active]:text-slate-900 data-[active]:shadow-lg hover:bg-slate-800"
								>
									<Squares2X2Icon className="h-5 w-5" />
									<span>Dashboard</span>
								</Tabs.Tab>
								<Tabs.Tab
									value="insights"
									className="w-full md:w-auto flex items-center justify-center space-x-2 px-2 sm:px-5 py-2.5 rounded-lg font-medium text-sm sm:text-base text-slate-200 transition-colors duration-150 data-[active]:bg-emerald-500 data-[active]:text-slate-900 data-[active]:shadow-lg hover:bg-slate-800"
								>
									<TrophyIcon className="h-5 w-5" />
									<span>Career Insights</span>
								</Tabs.Tab>
								<Tabs.Tab
									value="season-insights"
									className="w-full md:w-auto flex items-center justify-center space-x-2 px-2 sm:px-5 py-2.5 rounded-lg font-medium text-sm sm:text-base text-slate-200 transition-colors duration-150 data-[active]:bg-orange-400 data-[active]:text-slate-900 data-[active]:shadow-lg hover:bg-slate-800"
								>
									<CalendarIcon className="h-5 w-5" />
									<span>Season Insights</span>
								</Tabs.Tab>
								<Tabs.Tab
									value="statistics"
									className="w-full md:w-auto flex items-center justify-center space-x-2 px-2 sm:px-5 py-2.5 rounded-lg font-medium text-sm sm:text-base text-slate-200 transition-colors duration-150 data-[active]:bg-cyan-400 data-[active]:text-slate-900 data-[active]:shadow-lg hover:bg-slate-800"
								>
									<ChartBarIcon className="h-5 w-5" />
									<span>Statistics</span>
								</Tabs.Tab>
							</Tabs.List>
						</div>

						<div className="panel rounded-2xl overflow-hidden">
							<Tabs.Panel value="dashboard" className="p-0">
								<SeasonDashboard orgId={orgId} />
							</Tabs.Panel>

							<Tabs.Panel value="statistics" className="p-0">
								<TeamStats orgId={orgId} />
							</Tabs.Panel>

							<Tabs.Panel value="insights" className="p-0">
								<Insights orgId={orgId} />
							</Tabs.Panel>

							<Tabs.Panel value="season-insights" className="p-0">
								<SeasonInsights orgId={orgId} />
							</Tabs.Panel>
						</div>
					</Tabs>
				</div>
			</div>

			<Footer />
		</div>
	);
};

export default Stats;
